import { Link } from "react-router-dom";
import { Wordmark } from "@/components/Brand";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PROMISES, STACK } from "@/components/privacy/privacyContent";
import { ShieldCheck, Bell, Users, ArrowLeft, ExternalLink, Settings2 } from "lucide-react";

function ControlCard({ icon: Icon, title, text }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-foreground">
          <Icon className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">{title}</h3>
        </div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
        <p className="mt-3 inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary">
          Turn off anytime in Profile → Privacy
        </p>
      </CardContent>
    </Card>
  );
}

export default function Privacy() {
  return (
    <div className="fc-paper-noise min-h-screen bg-background" data-testid="privacy-page">
      {/* Top bar */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <Wordmark />
          <Button asChild variant="ghost" size="sm" data-testid="privacy-back-home">
            <Link to="/"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to site</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        {/* Hero */}
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <ShieldCheck className="h-3.5 w-3.5" /> Privacy &amp; Transparency
          </span>
          <h1 className="mt-4 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            Your data, your call.
          </h1>
          <p className="mt-3 text-base leading-7 text-muted-foreground">
            Friday Conversations is a small, locally owned community — not a big-tech social network.
            Here’s exactly what we collect, what we’d never do, and the tools that quietly run everything
            behind the scenes.
          </p>
        </div>

        {/* Promises */}
        <section className="mt-12">
          <h2 className="font-serif text-2xl font-semibold text-foreground">Our promises to you</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROMISES.map((p) => (
              <Card key={p.title} className="border-border bg-card" data-testid="privacy-promise-pillar">
                <CardContent className="p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-3 font-semibold text-foreground">{p.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{p.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* You're in control */}
        <section className="mt-14">
          <h2 className="font-serif text-2xl font-semibold text-foreground">You’re in control</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            The only things we ever do with your information are send you helpful notifications and help you
            network with other members — and both are entirely optional.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <ControlCard
              icon={Bell}
              title="Email & push notifications"
              text="Event reminders and “we’re live” alerts only go out if you opt in. Security messages (like a password reset you requested) are the only exception."
            />
            <ControlCard
              icon={Users}
              title="Member networking"
              text="Your profile appears in the member directory only when you choose to. Switch it off and you stay completely private."
            />
          </div>
          <div className="mt-6">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="privacy-manage-settings">
              <Link to="/portal/account/profile"><Settings2 className="mr-2 h-4 w-4" /> Manage my privacy settings</Link>
            </Button>
          </div>
        </section>

        {/* Where data lives & who built it */}
        <section className="mt-14">
          <h2 className="font-serif text-2xl font-semibold text-foreground">Where your data lives &amp; who built it</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            We believe in showing our work. Every tool below is run on the group’s own accounts.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {STACK.map((s) => (
              <div key={s.title} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4" data-testid="privacy-stack-item">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
                  <s.icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {s.text}
                    {s.href && (
                      <>
                        {" "}
                        <a href={s.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center font-medium text-primary hover:underline">
                          {s.linkLabel || s.href} <ExternalLink className="ml-0.5 h-3 w-3" />
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Closing note */}
        <section className="mt-14">
          <Card className="border-primary/25 bg-primary/5">
            <CardContent className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
                <div>
                  <p className="font-serif text-lg font-semibold text-foreground">Questions about your data?</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    You can export, hide, or permanently delete everything from your account at any time.
                  </p>
                </div>
              </div>
              <Button asChild variant="secondary" data-testid="privacy-join-cta">
                <Link to="/register">Join the group</Link>
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
